"use client";

import { ClipboardCheck } from "lucide-react";

import { CompulifeQuoteForm } from "@/app/(authenticated)/leads/_modules/components/CompulifeQuoteForm";
import CompulifeQuoteResults from "./CompulifeQuoteResults";
import type { CompulifeQuoteFormCardData } from "../../types/insurabot-cards";

const S =
  "rgba(0,0,0,0.06) 0px 0px 0px 1px, rgba(0,0,0,0.03) 0px 1px 2px, rgba(0,0,0,0.02) 0px 4px 12px -2px";

/**
 * Inline Compulife quote form rendered inside an InsuraBot chat message — the
 * non-Quote&Enroll counterpart of TechficientQuoteFormCard. Agencies WITHOUT
 * the quoteAndEnroll flag get this so their agents still get the clean in-chat
 * form experience (pre-filled from the lead), backed by Compulife.
 *
 * CompulifeQuoteForm reads its pre-fill from a `lead` object, so we synthesize
 * a minimal lead-shaped object from the card data the backend built.
 */
export default function CompulifeQuoteFormCard({
  data,
}: {
  data: CompulifeQuoteFormCardData;
}) {
  // Compulife submits against a leadId — without one there's nothing to quote.
  if (!data?.leadId) return null;

  // Synthesize the lead shape CompulifeQuoteForm reads (name/dob/gender/
  // state/zip/height/weight). Gender is normalized to the "Male"/"Female" /
  // single-letter forms the form's parser accepts.
  const synthesizedLead = {
    id: data.leadId,
    name: data.leadName || undefined,
    dob: data.birthDate || undefined,
    gender: data.gender || undefined,
    state: data.state || undefined,
    zipCode: data.zipCode || undefined,
    height: data.heightInches ?? undefined,
    weight: data.weightPounds ?? undefined,
  } as any;

  return (
    // w-full + min-w-0: bound the card to the chat message width so the
    // carrier scroll-row inside can't stretch it (and the form) super wide.
    <div
      className="mt-3 w-full min-w-0 max-w-full rounded-lg overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-500"
      style={{ boxShadow: S }}
    >
      <div className="bg-gradient-to-br from-primary/5 to-white px-4 py-3 border-b">
        <p className="text-[9px] font-bold text-primary uppercase tracking-[0.1em] inline-flex items-center gap-1">
          <ClipboardCheck className="h-3 w-3" />
          Quote builder
        </p>
        {data.hint && (
          <p className="text-[11px] text-gray-600 mt-1">{data.hint}</p>
        )}
      </div>
      <div className="bg-white px-4 py-3 min-w-0">
        <CompulifeQuoteForm leadId={data.leadId} lead={synthesizedLead} />
        {/* CompulifeQuoteForm relies on its parent to render results — in the
            chat there's no parent, so we render them here (shared component,
            same one the QuoteForm 403 fallback uses). */}
        <CompulifeQuoteResults leadId={data.leadId} />
      </div>
    </div>
  );
}
